// Static demo-mode fill-in data for Alerts.component.jsx.
//
// Alerts.component.jsx normally builds three pieces of state from live
// backend calls:
//   - teamOptions        <- teamService.getMemberTeamCollection (prepended
//                            with the hardcoded `allTeamsOption` already
//                            defined in the component)
//   - employeeOptions    <- useTeamMemberDetailsForSelect (prepended with
//                            the hardcoded `allEmployeesOption`)
//   - enrichedAlerts     <- alertsService.getUserAlerts, then enriched with
//                            teamName/employeeName/currentProductivity/
//                            expectedProductivity via async lookups
//
// These fixtures mirror only the "extra" options (the all-teams/all-employees
// entries stay defined once, in the component) and the final *enriched*
// alert shape consumed directly by Alerts.config.jsx's alertsTableConfig
// (employeeName, teamName, currentProductivity, expectedProductivity,
// createdAt) plus the raw fields (id, teamId, userId, title, severity,
// isRead, metadata) other alert-related UI (e.g. the notification bell)
// reads elsewhere in the app.
//
// Names/ids/emails/teams match the canonical roster in
// EmployeeList.demoData.js. `createdAt` is computed relative to "now" (see
// `relativeIso` below) so the alert feed never goes stale — mirrors the
// pattern used in TeamsTimeline.demoData.js / UserAttendance.demoData.js.

const DAY_MS = 24 * 60 * 60 * 1000;
const relativeIso = (daysAgo, hh, mm) => {
  const d = new Date(Date.now() - daysAgo * DAY_MS);
  d.setUTCHours(hh, mm, 0, 0);
  return d.toISOString();
};

export const DEMO_TEAM_OPTIONS = [
  { value: 'demo-team-engineering', label: 'Engineering' },
  { value: 'demo-team-sales', label: 'Sales' },
  { value: 'demo-team-support', label: 'Customer Support' },
];

export const DEMO_EMPLOYEE_OPTIONS = [
  { value: 'demo-user', label: 'Ryan Mitchell' },
  { value: 'demo-user-2', label: 'Rachel Turner' },
  { value: 'demo-user-3', label: 'Michael Chen' },
  { value: 'demo-user-4', label: 'Sarah Johnson' },
  { value: 'demo-user-5', label: 'Wei Zhang' },
];

// Final "enriched" shape — the same shape `enrichAlerts()` produces from raw
// alerts — so it can be dropped in wherever `enrichedAlerts` is consumed
// (filteredAlerts/groupedAlerts derive from it unchanged).
export const DEMO_ALERTS = [
  {
    id: 'demo-alert-1',
    teamId: 'demo-team-engineering',
    userId: 'demo-user',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(1, 8, 45),
    metadata: {
      productivityPercentage: 42.3,
      threshold: 70,
      userEmailId: 'ryan.mitchell@meridianlabs.com',
    },
    employeeName: 'Ryan Mitchell',
    teamName: 'Engineering',
    currentProductivity: 42.3,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-2',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-3',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: true,
    createdAt: relativeIso(2, 14, 20),
    metadata: {
      productivityPercentage: 58.1,
      threshold: 65,
      userEmailId: 'michael.chen@meridianlabs.com',
    },
    employeeName: 'Michael Chen',
    teamName: 'Engineering',
    currentProductivity: 58.1,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-3',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-2',
    title: 'Productivity dropped below threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(3, 10, 5),
    metadata: {
      productivityPercentage: 61.8,
      threshold: 65,
      userEmailId: 'rachel.turner@meridianlabs.com',
    },
    employeeName: 'Rachel Turner',
    teamName: 'Engineering',
    currentProductivity: 61.8,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-4',
    teamId: 'demo-team-sales',
    userId: 'demo-user-4',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(1, 6, 10),
    metadata: {
      productivityPercentage: 35.0,
      threshold: 70,
      userEmailId: 'sarah.johnson@meridianlabs.com',
    },
    employeeName: 'Sarah Johnson',
    teamName: 'Sales',
    currentProductivity: 35.0,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-5',
    teamId: 'demo-team-sales',
    userId: 'demo-user-5',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: false,
    createdAt: relativeIso(2, 19, 30),
    metadata: {
      productivityPercentage: 55.4,
      threshold: 65,
      userEmailId: 'wei.zhang@meridianlabs.com',
    },
    employeeName: 'Wei Zhang',
    teamName: 'Sales',
    currentProductivity: 55.4,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-6',
    teamId: 'demo-team-engineering',
    userId: 'demo-user',
    title: 'Productivity back above threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(4, 9, 0),
    metadata: {
      productivityPercentage: 74.2,
      threshold: 70,
      userEmailId: 'ryan.mitchell@meridianlabs.com',
    },
    employeeName: 'Ryan Mitchell',
    teamName: 'Engineering',
    currentProductivity: 74.2,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-7',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-2',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(1, 7, 55),
    metadata: {
      productivityPercentage: 39.6,
      threshold: 70,
      userEmailId: 'rachel.turner@meridianlabs.com',
    },
    employeeName: 'Rachel Turner',
    teamName: 'Engineering',
    currentProductivity: 39.6,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-8',
    teamId: 'demo-team-engineering',
    userId: 'demo-user',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(0, 7, 30),
    metadata: {
      productivityPercentage: 38.9,
      threshold: 70,
      userEmailId: 'ryan.mitchell@meridianlabs.com',
    },
    employeeName: 'Ryan Mitchell',
    teamName: 'Engineering',
    currentProductivity: 38.9,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-9',
    teamId: 'demo-team-engineering',
    userId: 'demo-user',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: true,
    createdAt: relativeIso(5, 11, 15),
    metadata: {
      productivityPercentage: 60.2,
      threshold: 65,
      userEmailId: 'ryan.mitchell@meridianlabs.com',
    },
    employeeName: 'Ryan Mitchell',
    teamName: 'Engineering',
    currentProductivity: 60.2,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-10',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-3',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(0, 9, 10),
    metadata: {
      productivityPercentage: 45.0,
      threshold: 70,
      userEmailId: 'michael.chen@meridianlabs.com',
    },
    employeeName: 'Michael Chen',
    teamName: 'Engineering',
    currentProductivity: 45.0,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-11',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-3',
    title: 'Productivity dropped below threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(4, 16, 40),
    metadata: {
      productivityPercentage: 63.5,
      threshold: 65,
      userEmailId: 'michael.chen@meridianlabs.com',
    },
    employeeName: 'Michael Chen',
    teamName: 'Engineering',
    currentProductivity: 63.5,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-12',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-3',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: true,
    createdAt: relativeIso(6, 13, 25),
    metadata: {
      productivityPercentage: 59.8,
      threshold: 65,
      userEmailId: 'michael.chen@meridianlabs.com',
    },
    employeeName: 'Michael Chen',
    teamName: 'Engineering',
    currentProductivity: 59.8,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-13',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-2',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: false,
    createdAt: relativeIso(0, 8, 5),
    metadata: {
      productivityPercentage: 56.7,
      threshold: 65,
      userEmailId: 'rachel.turner@meridianlabs.com',
    },
    employeeName: 'Rachel Turner',
    teamName: 'Engineering',
    currentProductivity: 56.7,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-14',
    teamId: 'demo-team-engineering',
    userId: 'demo-user-2',
    title: 'Productivity back above threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(5, 15, 50),
    metadata: {
      productivityPercentage: 68.9,
      threshold: 65,
      userEmailId: 'rachel.turner@meridianlabs.com',
    },
    employeeName: 'Rachel Turner',
    teamName: 'Engineering',
    currentProductivity: 68.9,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-15',
    teamId: 'demo-team-sales',
    userId: 'demo-user-4',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: false,
    createdAt: relativeIso(0, 10, 20),
    metadata: {
      productivityPercentage: 52.3,
      threshold: 65,
      userEmailId: 'sarah.johnson@meridianlabs.com',
    },
    employeeName: 'Sarah Johnson',
    teamName: 'Sales',
    currentProductivity: 52.3,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-16',
    teamId: 'demo-team-sales',
    userId: 'demo-user-4',
    title: 'Productivity back above threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(3, 9, 40),
    metadata: {
      productivityPercentage: 66.1,
      threshold: 65,
      userEmailId: 'sarah.johnson@meridianlabs.com',
    },
    employeeName: 'Sarah Johnson',
    teamName: 'Sales',
    currentProductivity: 66.1,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-17',
    teamId: 'demo-team-sales',
    userId: 'demo-user-4',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: true,
    createdAt: relativeIso(6, 14, 15),
    metadata: {
      productivityPercentage: 31.8,
      threshold: 70,
      userEmailId: 'sarah.johnson@meridianlabs.com',
    },
    employeeName: 'Sarah Johnson',
    teamName: 'Sales',
    currentProductivity: 31.8,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-18',
    teamId: 'demo-team-sales',
    userId: 'demo-user-5',
    title: 'Productivity dropped below threshold',
    severity: 'high',
    isRead: false,
    createdAt: relativeIso(0, 6, 50),
    metadata: {
      productivityPercentage: 40.4,
      threshold: 70,
      userEmailId: 'wei.zhang@meridianlabs.com',
    },
    employeeName: 'Wei Zhang',
    teamName: 'Sales',
    currentProductivity: 40.4,
    expectedProductivity: 70,
  },
  {
    id: 'demo-alert-19',
    teamId: 'demo-team-sales',
    userId: 'demo-user-5',
    title: 'Productivity back above threshold',
    severity: 'low',
    isRead: true,
    createdAt: relativeIso(4, 12, 30),
    metadata: {
      productivityPercentage: 67.2,
      threshold: 65,
      userEmailId: 'wei.zhang@meridianlabs.com',
    },
    employeeName: 'Wei Zhang',
    teamName: 'Sales',
    currentProductivity: 67.2,
    expectedProductivity: 65,
  },
  {
    id: 'demo-alert-20',
    teamId: 'demo-team-sales',
    userId: 'demo-user-5',
    title: 'Productivity dropped below threshold',
    severity: 'medium',
    isRead: true,
    createdAt: relativeIso(6, 17, 5),
    metadata: {
      productivityPercentage: 57.9,
      threshold: 65,
      userEmailId: 'wei.zhang@meridianlabs.com',
    },
    employeeName: 'Wei Zhang',
    teamName: 'Sales',
    currentProductivity: 57.9,
    expectedProductivity: 65,
  },
];
